import { useEffect, useRef, type MutableRefObject } from 'react';

interface UseInfiniteScrollTriggerOptions {
  /** Whether there are more pages available to fetch. */
  hasNextPage: boolean | undefined;
  /** Whether a page fetch is currently in flight. */
  isFetchingNextPage: boolean;
  /** Function to call when the sentinel enters the viewport. */
  fetchNextPage: () => void;
  /** IntersectionObserver rootMargin — how early to trigger (default: 200px). */
  rootMargin?: string;
}

/**
 * Returns a ref to attach to a sentinel element at the bottom of a scrollable list.
 * When the sentinel enters the viewport, `fetchNextPage` is called automatically.
 *
 * @example
 * ```tsx
 * const sentinelRef = useInfiniteScrollTrigger({
 *   hasNextPage,
 *   isFetchingNextPage,
 *   fetchNextPage,
 * });
 *
 * return (
 *   <>
 *     {cards}
 *     <div ref={sentinelRef} />
 *   </>
 * );
 * ```
 */
export function useInfiniteScrollTrigger({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = '200px', // standards-ignore: IntersectionObserver margin string, not element styling
}: UseInfiniteScrollTriggerOptions): MutableRefObject<HTMLDivElement | null> {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rootMargin]);

  return sentinelRef;
}
